import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaArrowLeft, FaEnvelope, FaPhone, FaMapMarkerAlt, FaCalendar, FaBox, FaMusic, FaMicrophone } from 'react-icons/fa';
import axios from 'axios';
import '../styles/BookingDetail.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function CustomerDetail() {
  const { email } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCustomer();
  }, [email]);

  const loadCustomer = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/customers/${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomer(response.data.customer);
    } catch (error) {
      console.error('Error loading customer:', error);
      alert('Fehler beim Laden des Kunden');
      navigate('/kunden');
    } finally {
      setLoading(false);
    }
  };

  const getBookingTypeIcon = (type) => {
    const types = {
      feierpaket: { icon: <FaBox />, color: '#dc2626', label: 'Feierpaket' },
      dj: { icon: <FaMusic />, color: '#7c3aed', label: 'DJ' },
      livemusik: { icon: <FaMicrophone />, color: '#059669', label: 'Livemusik' }
    };
    return types[type] || types.feierpaket;
  };

  const getStatusColor = (status) => {
    const statusMap = {
      'Neu': 'neu',
      'Angefragt': 'angefragt',
      'In Bearbeitung': 'angefragt',
      'Angebot erstellt': 'angefragt',
      'Bestätigt': 'bestaetigt',
      'Bezahlt': 'bestaetigt',
      'Versendet': 'versendet',
      'Zurück': 'zurueck',
      'Abgeschlossen': 'abgeschlossen',
      'Abgelehnt': 'zurueck'
    };
    return statusMap[status] || 'neu';
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  if (!customer) {
    return <div className="card">Kunde nicht gefunden</div>;
  }

  const totalBookings = (customer.bookings?.feierpakete?.length || 0) + 
                       (customer.bookings?.dj?.length || 0) + 
                       (customer.bookings?.livemusik?.length || 0);

  const totalRevenue = [
    ...(customer.bookings?.feierpakete || []),
    ...(customer.bookings?.dj || []),
    ...(customer.bookings?.livemusik || [])
  ].reduce((sum, b) => {
    const preis = b.Preis || b['Manueller Preis aus CRM'] || b.Gesamtpreis || 0;
    return sum + parseFloat(preis);
  }, 0);

  return (
    <div className="booking-detail-page">
      <div className="page-header">
        <div>
          <Link to="/kunden" className="back-button">
            <FaArrowLeft /> Zurück zur Übersicht
          </Link>
          <h1>👤 {customer.vorname} {customer.nachname}</h1>
        </div>
      </div>

      {/* Kundendaten */}
      <div className="info-grid">
        <div className="card info-card">
          <h2>Kontaktdaten</h2>
          <div className="info-row">
            <FaEnvelope />
            <div>
              <label>E-Mail</label>
              <a href={`mailto:${customer.email}`}>{customer.email}</a>
            </div>
          </div>
          {customer.telefon && (
            <div className="info-row">
              <FaPhone />
              <div>
                <label>Telefon</label>
                <a href={`tel:${customer.telefon}`}>{customer.telefon}</a>
              </div>
            </div>
          )}
          {customer.ort && (
            <div className="info-row">
              <FaMapMarkerAlt />
              <div>
                <label>Ort</label>
                <p>{customer.ort}, {customer.land}</p>
              </div>
            </div>
          )}
        </div>

        <div className="card info-card">
          <h2>Statistiken</h2>
          <div className="info-row">
            <FaCalendar />
            <div>
              <label>Anzahl Buchungen</label>
              <p><strong>{totalBookings}</strong></p>
            </div>
          </div>
          <div className="info-row">
            <div style={{ fontSize: '1.5rem' }}>💶</div>
            <div>
              <label>Gesamtumsatz</label>
              <p className="text-primary"><strong>{totalRevenue.toFixed(0)}€</strong></p>
            </div>
          </div>
        </div>
      </div>

      {/* Buchungshistorie - Feierpakete */}
      {customer.bookings?.feierpakete?.length > 0 && (
        <div className="card">
          <h2 className="mb-3">📦 Feierpaket-Buchungen ({customer.bookings.feierpakete.length})</h2>
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Preis</th>
                  <th>Status</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {customer.bookings.feierpakete.map(booking => (
                  <tr key={booking.id}>
                    <td>
                      {booking.Eventdatum ? 
                        new Date(booking.Eventdatum).toLocaleDateString('de-DE') : 
                        (booking.Reservierung_ablauf ? new Date(booking.Reservierung_ablauf).toLocaleDateString('de-DE') : '-')
                      }
                    </td>
                    <td className="text-primary">
                      <strong>{booking.Preis ? `${booking.Preis}€` : '-'}</strong>
                    </td>
                    <td>
                      <span className={`status-badge status-${getStatusColor(booking.Status)}`}>
                        {booking.Status || 'Neu'}
                      </span>
                    </td>
                    <td>
                      <Link to={`/feierpakete/${booking.id}`} className="btn btn-sm btn-secondary">
                        Details
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Buchungshistorie - DJ */}
      {customer.bookings?.dj?.length > 0 && (
        <div className="card">
          <h2 className="mb-3">🎧 DJ-Buchungen ({customer.bookings.dj.length})</h2>
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Event-Datum</th>
                  <th>Event-Typ</th>
                  <th>Preis</th>
                  <th>Status</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {customer.bookings.dj.map(booking => (
                  <tr key={booking.id}>
                    <td>
                      {booking['Event-Datum'] ? 
                        new Date(booking['Event-Datum']).toLocaleDateString('de-DE') : 
                        '-'
                      }
                    </td>
                    <td>{booking['Event-Typ']?.[0] || '-'}</td>
                    <td className="text-primary">
                      <strong>
                        {(booking['Manueller Preis aus CRM'] || booking.Gesamtpreis) 
                          ? `${booking['Manueller Preis aus CRM'] || booking.Gesamtpreis}€` 
                          : '-'}
                      </strong>
                    </td>
                    <td>
                      <span className={`status-badge status-${getStatusColor(booking.Buchungsstatus)}`}>
                        {booking.Buchungsstatus || 'Neu'}
                      </span>
                    </td>
                    <td>
                      <Link to={`/dj/${booking.id}`} className="btn btn-sm btn-secondary">
                        Details
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Buchungshistorie - Livemusik */}
      {customer.bookings?.livemusik?.length > 0 && (
        <div className="card">
          <h2 className="mb-3">🎤 Livemusik-Buchungen ({customer.bookings.livemusik.length})</h2>
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Event-Datum</th>
                  <th>Event-Typ</th>
                  <th>Preis</th>
                  <th>Status</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {customer.bookings.livemusik.map(booking => (
                  <tr key={booking.id}>
                    <td>
                      {booking['Event-Datum'] ? 
                        new Date(booking['Event-Datum']).toLocaleDateString('de-DE') : 
                        '-'
                      }
                    </td>
                    <td>{booking['Event-Typ']?.[0] || '-'}</td>
                    <td className="text-primary">
                      <strong>
                        {(booking['Manueller Preis aus CRM'] || booking.Gesamtpreis) 
                          ? `${booking['Manueller Preis aus CRM'] || booking.Gesamtpreis}€` 
                          : '-'}
                      </strong>
                    </td>
                    <td>
                      <span className={`status-badge status-${getStatusColor(booking.Buchungsstatus)}`}>
                        {booking.Buchungsstatus || 'Neu'}
                      </span>
                    </td>
                    <td>
                      <Link to={`/livemusik/${booking.id}`} className="btn btn-sm btn-secondary">
                        Details
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerDetail;
